/**
 * New Permissions API for JSON-based RBAC system
 * Works with the new backend permission endpoints
 * Uses the standard API helper pattern consistent with other Sleep service APIs
 */

import { apiGet, apiPost, apiPut, apiDelete, getAuthToken } from '../../Restore/library/helpers/apiHelper';
import cookieHelper from '../../Restore/library/helpers/cookieHelper';

// =====================================================
// USER PERMISSION APIS
// =====================================================

// User-scoped in-memory cache for user permissions to prevent multiple redundant network fetches on refresh/mount
let cachedUserPermissions = null;
let cachedForToken = null;
let cachedForStaffId = null;
let lastPermissionsFetch = 0;
const USER_PERMISSIONS_TTL = 60 * 1000; // 60 seconds

export const invalidateCurrentUserPermissionsCache = () => {
  cachedUserPermissions = null;
  cachedForToken = null;
  cachedForStaffId = null;
  lastPermissionsFetch = 0;
};

if (typeof window !== 'undefined') {
  window.addEventListener('permissionsUpdated', invalidateCurrentUserPermissionsCache);
  window.addEventListener('userLoggedOut', invalidateCurrentUserPermissionsCache);
  window.addEventListener('authStateChanged', invalidateCurrentUserPermissionsCache);
}

/**
 * Get current user's permissions across all services
 * @returns {Promise<Object>} - User permissions object
 */
export const getCurrentUserPermissions = async () => {
  try {
    const now = Date.now();
    const currentToken = getAuthToken();
    const userDetails = cookieHelper.getUserDetails();
    const currentStaffId = userDetails?.staffId || userDetails?.staff_id || '';

    // Cache is only valid for the exact same user session and token
    const isSameUser = currentToken &&
      cachedForToken === currentToken &&
      String(cachedForStaffId) === String(currentStaffId);

    if (cachedUserPermissions && isSameUser && (now - lastPermissionsFetch < USER_PERMISSIONS_TTL)) {
      return cachedUserPermissions;
    }

    const response = await apiGet('api/v1/permissions/user-permissions');
    const result = response.success ? response.data : {};
    if (response.success && result && Object.keys(result).length > 0 && getAuthToken() === currentToken) {
      cachedUserPermissions = result;
      cachedForToken = currentToken;
      cachedForStaffId = currentStaffId;
      lastPermissionsFetch = now;
    }
    return result;
  } catch (error) {
    console.error('Error fetching current user permissions:', error);
    return cachedUserPermissions || {};
  }
};

/**
 * Get permissions for a specific user
 * @param {number} staffId - Staff ID
 * @returns {Promise<Object>} - User permissions object
 */
export const getUserPermissions = async (staffId) => {
  try {
    const response = await apiGet(`api/v1/permissions/user/${staffId}`);
 
    return response.success ? response.data : {};
  } catch (error) {
    console.error(`Error fetching permissions for user ${staffId}:`, error);
    return {};
  }
};

/**
 * Check if user has specific permission
 * @param {Object} permissionData - { staffId, serviceId, moduleName, action }
 * @returns {Promise<boolean>} - True if user has permission
 */
export const checkUserPermission = async (permissionData) => {
  try {
    const response = await apiPost('api/v1/permissions/check', permissionData);
    return response.success ? response.hasPermission : false;
  } catch (error) {
    console.error('Error checking user permission:', error);
    return false;
  }
};

/**
 * Get all services user has access to
 * @returns {Promise<Array>} - Array of services
 */
export const getUserServices = async () => {
  try {
    const response = await apiGet('api/v1/permissions/services');
    return response.success ? response.data : [];
  } catch (error) {
    console.error('Error fetching user services:', error);
    return [];
  }
};

/**
 * Get all roles for a specific service
 * @param {number} serviceId - Service ID
 * @returns {Promise<Array>} - Array of service roles
 */
export const getServiceRoles = async (serviceId) => {
  try {
    const response = await apiGet(`api/v1/permissions/services/${serviceId}/roles`);
    return response.success ? response.data : [];
  } catch (error) {
    console.error(`Error fetching roles for service ${serviceId}:`, error);
    return [];
  }
};

/**
 * Get ALL service roles (no filtering - for Admin Console)
 * @param {number} serviceId - Service ID
 * @returns {Promise<Array>} - Array of ALL roles
 */
export const getAllServiceRoles = async (serviceId) => {
  try {
    const response = await apiGet(`api/v1/permissions/services/${serviceId}/all-roles`);
    return response;
  } catch (error) {
    console.error(`Error fetching all roles for service ${serviceId}:`, error);
    throw error;
  }
};

/**
 * Get modules user has access to in a specific service
 * @param {number} serviceId - Service ID
 * @returns {Promise<Array>} - Array of modules
 */
export const getUserModules = async (serviceId) => {
  try {
    const response = await apiGet(`api/v1/permissions/services/${serviceId}/user-modules`);
    return response.success ? response.data : [];
  } catch (error) {
    console.error(`Error fetching user modules for service ${serviceId}:`, error);
    return [];
  }
};

// =====================================================
// ADMIN PERMISSION MANAGEMENT APIS
// =====================================================

/**
 * Get all available modules for a service
 * @param {number} serviceId - Service ID
 * @returns {Promise<Array>} - Array of available modules
 */
export const getAvailableModules = async (serviceId) => {
  try {
    const response = await apiGet(`api/v1/permissions/services/${serviceId}/modules`);
    return response.success ? response.data : [];
  } catch (error) {
    console.error(`Error fetching available modules for service ${serviceId}:`, error);
    return [];
  }
};

/**
 * Get permissions for a specific role and service
 * @param {number} roleId - Role ID
 * @param {number} serviceId - Service ID
 * @returns {Promise<Array>} - Array of role permissions
 */
export const getRolePermissions = async (roleId, serviceId) => {
  try {
    const response = await apiGet(`api/v1/permissions/services/${serviceId}/roles/${roleId}`);
    return response.success ? response.data : [];
  } catch (error) {
    console.error(`Error fetching role permissions for role ${roleId} in service ${serviceId}:`, error);
    return [];
  }
};

/**
 * Update permissions for a specific role and module
 * @param {number} roleId - Role ID
 * @param {number} serviceId - Service ID
 * @param {string} moduleName - Module name
 * @param {Object} permissions - Permissions object { read, write, edit, delete }
 * @returns {Promise<Object>} - Updated permission data
 */
export const updateModulePermissions = async (roleId, serviceId, moduleName, permissions) => {
  try {
    const response = await apiPut(`api/v1/permissions/services/${serviceId}/roles/${roleId}/module`, {
      moduleName,
      permissions
    });
    return response.success ? response.data : null;
  } catch (error) {
    console.error('Error updating module permissions:', error);
    return null;
  }
};

/**
 * Bulk update permissions for a role across multiple modules
 * @param {number} roleId - Role ID
 * @param {number} serviceId - Service ID
 * @param {Array} permissions - Array of permission objects
 * @returns {Promise<Object>} - Update result
 */
export const bulkUpdateRolePermissions = async (roleId, serviceId, permissions) => {
  try {
    const response = await apiPut(`api/v1/permissions/services/${serviceId}/roles/${roleId}/bulk`, {
      permissions
    });
    return response.success ? response.data : null;
  } catch (error) {
    console.error('Error bulk updating role permissions:', error);
    return null;
  }
};

// =====================================================
// STAFF ROLE MANAGEMENT APIS
// =====================================================

/**
 * Assign a role to a staff member
 * @param {number} staffId - Staff ID
 * @param {number} roleId - Role ID
 * @param {number} serviceId - Service ID
 * @returns {Promise<Object>} - Assignment result
 */
export const assignStaffRole = async (staffId, roleId, serviceId) => {
  try {
    const response = await apiPost('api/v1/permissions/staff/assign-role', {
      staffId,
      roleId,
      serviceId
    });
    return response.success ? response.data : null;
  } catch (error) {
    console.error('Error assigning staff role:', error);
    return null;
  }
};

/**
 * Remove a role from a staff member
 * @param {number} staffId - Staff ID
 * @param {number} roleId - Role ID
 * @param {number} serviceId - Service ID
 * @returns {Promise<Object>} - Removal result
 */
export const removeStaffRole = async (staffId, roleId, serviceId) => {
  try {
    const response = await apiPost('api/v1/permissions/staff/remove-role', {
      staffId,
      roleId,
      serviceId
    });
    return response.success ? response.data : null;
  } catch (error) {
    console.error('Error removing staff role:', error);
    return null;
  }
};

/**
 * Assign multiple roles to a staff member for a specific service (bulk assignment)
 * @param {number} staffId - Staff ID
 * @param {Array<number>} roleIds - Array of role IDs to assign
 * @param {number} serviceId - Service ID
 * @returns {Promise<Object>} - Assignment result
 */
export const assignMultipleStaffRoles = async (staffId, roleIds, serviceId) => {
  try {
    const response = await apiPost('api/v1/permissions/staff/assign-multiple-roles', {
      staffId,
      roleIds,
      serviceId
    });
    
    if (!response.success) {
      throw new Error(response.message || response.error || 'Failed to assign roles');
    }
    
    return response.data || response;
  } catch (error) {
    console.error('Error assigning multiple staff roles:', error);
    // Re-throw so caller can handle it
    throw error;
  }
};

/**
 * Get all staff members with their roles for a service.
 * Default: only staff who already have an active role in this service (Staff-Role Mapping).
 * Pass includeUnassigned=true for Access Control User Overrides so members who belong to the
 * service via primary_service_id / staff_service_map but have no role yet are not dropped.
 * @param {number} serviceId - Service ID
 * @param {{ includeUnassigned?: boolean }} [options]
 * @returns {Promise<Array>} - Array of staff with roles
 */
export const getServiceStaffRoles = async (serviceId, { includeUnassigned = false } = {}) => {
  try {
    const response = await apiGet(
      `api/v1/permissions/services/${serviceId}/staff-roles`,
      includeUnassigned ? { includeUnassigned: true } : {}
    );
    return response.success ? response.data : [];
  } catch (error) {
    console.error(`Error fetching staff roles for service ${serviceId}:`, error);
    return [];
  }
};

/**
 * Get RT staff for DME service (DME-specific endpoint)
 * @returns {Promise<Array>} - Array of RT staff with roles
 */
export const getDMERTStaff = async () => {
  try {
    const response = await apiGet('api/v1/permissions/dme/rt-staff');
    return response.success ? response.data : [];
  } catch (error) {
    console.error('Error fetching DME RT staff:', error);
    return [];
  }
};

// =====================================================
// ANALYTICS & REPORTING APIS
// =====================================================

/**
 * Get permission summary for a service
 * @param {number} serviceId - Service ID
 * @returns {Promise<Object>} - Permission summary data
 */
export const getPermissionSummary = async (serviceId) => {
  try {
    const response = await apiGet(`api/v1/permissions/services/${serviceId}/summary`);
    return response.success ? response.data : {};
  } catch (error) {
    console.error(`Error fetching permission summary for service ${serviceId}:`, error);
    return {};
  }
};

/**
 * Export permissions template for a service
 * @param {number} serviceId - Service ID
 * @returns {Promise<Array>} - Template data
 */
export const exportPermissionsTemplate = async (serviceId) => {
  try {
    const response = await apiGet(`api/v1/permissions/services/${serviceId}/template`);
    return response.success ? response.data : [];
  } catch (error) {
    console.error(`Error exporting permissions template for service ${serviceId}:`, error);
    return [];
  }
};

// =====================================================
// CACHE MANAGEMENT APIS
// =====================================================

/**
 * Clear permission cache
 * @param {number} staffId - Optional staff ID to clear specific user cache
 * @returns {Promise<Object>} - Clear result
 */
export const clearPermissionCache = async (staffId = null) => {
  try {
    invalidateCurrentUserPermissionsCache();
    const body = staffId ? { staffId } : {};
    const response = await apiPost('api/v1/permissions/cache/clear', body);
    return response.success ? response : null;
  } catch (error) {
    console.error('Error clearing permission cache:', error);
    return null;
  }
};

// =====================================================
// LEGACY COMPATIBILITY FUNCTIONS
// =====================================================

/**
 * Legacy save permissions function (for backward compatibility)
 * @param {string} roleKey - Role key
 * @param {Array} permissions - Array of permission objects
 * @param {string} serviceKey - Service key
 * @returns {Promise<Object>} - Save result
 */
export const savePermissions = async (roleKey, permissions, serviceKey) => {
  try {
    // Map legacy keys to IDs
    const roleMapping = {
      'admin': 4,
      'technician': 5,
      'office_staff': 6
    };
    
    const serviceMapping = {
      'sleep': 2,
      'restore': 1
    };

    const roleId = roleMapping[roleKey] || 4;
    const serviceId = serviceMapping[serviceKey] || 2;

    // Transform legacy format to new format
    const transformedPermissions = permissions.map(perm => ({
      moduleName: perm.module_name || perm.module_id,
      permissions: {
        read: perm.can_view || false,
        write: perm.can_create || false,
        edit: perm.can_edit || false,
        delete: perm.can_delete || false
      }
    }));

    return await bulkUpdateRolePermissions(roleId, serviceId, transformedPermissions);
  } catch (error) {
    console.error('Error in legacy save permissions:', error);
    return null;
  }
};

/**
 * Legacy get permissions function (for backward compatibility)
 * @param {string} roleKey - Role key
 * @param {string} serviceKey - Service key
 * @returns {Promise<Array>} - Permissions array
 */
export const getPermissions = async (roleKey, serviceKey) => {
  try {
    // Map legacy keys to IDs
    const roleMapping = {
      'admin': 4,
      'technician': 5,
      'office_staff': 6
    };
    
    const serviceMapping = {
      'sleep': 2,
      'restore': 1
    };

    const roleId = roleMapping[roleKey] || 4;
    const serviceId = serviceMapping[serviceKey] || 2;

    const permissions = await getRolePermissions(roleId, serviceId);

    // Transform to legacy format
    return permissions.map(perm => ({
      module_name: perm.module_name,
      module_key: perm.module_key,
      can_view: perm.permissions.read,
      can_create: perm.permissions.write,
      can_edit: perm.permissions.edit,
      can_delete: perm.permissions.delete
    }));
  } catch (error) {
    console.error('Error in legacy get permissions:', error);
    return [];
  }
};

// =====================================================
// SLEEP-SPECIFIC CONVENIENCE FUNCTIONS
// =====================================================

/**
 * Get Sleep service permissions for current user
 * @returns {Promise<Object>} - Sleep permissions object
 */
export const getSleepPermissions = async () => {
  try {
    const allPermissions = await getCurrentUserPermissions();
    return allPermissions[2] || {}; // Service ID 2 is Sleep
  } catch (error) {
    console.error('Error fetching Sleep permissions:', error);
    return {};
  }
};

/**
 * Check if user can view Sleep dashboard
 * @returns {Promise<boolean>} - True if user has dashboard access
 */
export const canViewDashboard = async () => {
  try {
    return await checkUserPermission({
      serviceId: 2,
      moduleName: 'Dashboard',
      action: 'read'
    });
  } catch (error) {
    console.error('Error checking dashboard permission:', error);
    return false;
  }
};

/**
 * Check if user can manage Sleep orders
 * @param {string} action - Action to check (read, write, edit, delete)
 * @returns {Promise<boolean>} - True if user has permission
 */
export const canManageOrders = async (action = 'read') => {
  try {
    return await checkUserPermission({
      serviceId: 2,
      moduleName: 'Orders',
      action
    });
  } catch (error) {
    console.error('Error checking orders permission:', error);
    return false;
  }
};

/**
 * Check if user can manage Sleep patients
 * @param {string} action - Action to check (read, write, edit, delete)
 * @returns {Promise<boolean>} - True if user has permission
 */
export const canManagePatients = async (action = 'read') => {
  try {
    return await checkUserPermission({
      serviceId: 2,
      moduleName: 'Patients',
      action
    });
  } catch (error) {
    console.error('Error checking patients permission:', error);
    return false;
  }
};

export default {
  // User permission APIs
  getCurrentUserPermissions,
  getUserPermissions,
  checkUserPermission,
  getUserServices,
  getServiceRoles,
  getUserModules,
  
  // Admin APIs
  getAvailableModules,
  getRolePermissions,
  updateModulePermissions,
  bulkUpdateRolePermissions,
  
  // Staff role management
  assignStaffRole,
  removeStaffRole,
  getServiceStaffRoles,
  
  // Analytics
  getPermissionSummary,
  exportPermissionsTemplate,
  
  // Cache management
  clearPermissionCache,
  
  // Legacy compatibility
  savePermissions,
  getPermissions,
  
  // Sleep-specific convenience functions
  getSleepPermissions,
  canViewDashboard,
  canManageOrders,
  canManagePatients
}; 